import {
  IFilterType,
  ISpreadsheetData,
  ITableRowInGroup,
  rowHeights,
  IGroupRowCreate,
  IGroupCollapsed,
  ITreeGroupRow,
  IAggregateTypes,
  IColumnValuesCount,
  IViewCoordinateMap,
  IColumnTypes,
  IResultFieldValidation
} from 'components/spreadsheet/types'
import {
  ITableViewFilter,
  ITableViewRowLock,
  ResponsePayload,
  ITableViewWithColumns,
  ITableRow,
  ITableColumn,
  ISummaryUser,
  ITableViewSort,
  ITableRowCompressed,
  IVote,
  ITableViewColumn,
  IAccessPolicy,
  ITag,
  IProcessObject,
  ICellValue,
  EditorContent,
  ITableViewColour,
  ITableViewChart,
  ITableViewHiddenColumn,
  CHARTS_WITH_GROUPING,
  IColumnarBatch
} from 'types'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { decodePublicIds, decodeSortOrders } from 'components/spreadsheet/helpers/columnar-stream'
import { getCaseInsensitiveDuplicateColumnIds, getDuplicateValueKey } from 'components/spreadsheet/helpers/duplicates'
import {
  TEXT_COLUMN_TYPES,
  NUMBER_COLUMN_TYPES,
  ATTACHMENT_COLUMN_TYPES,
  DATE_COLUMN_TYPES,
  LINK_COLUMN_TYPES,
  hasSyntheticColumns,
  getInitialSpreadsheetState,
  DEFAULT_COLUMN_TYPE,
  GROUP_KEY_SPLIT
} from 'components/spreadsheet/constants/const'
import {
  ROW_NUMBER_COLUMN_WIDTH,
  ROW_HEIGHTS,
  SMALL_ROW_HEIGHT_CLASS,
  MEDIUM_ROW_HEIGHT_CLASS,
  TALL_ROW_HEIGHT_CLASS,
  EXTRA_TALL_ROW_HEIGHT_CLASS,
  SMALL_ROW_MAX_HEIGHT_CLASS,
  MEDIUM_ROW_MAX_HEIGHT_CLASS,
  TALL_ROW_MAX_HEIGHT_CLASS,
  EXTRA_TALL_ROW_MAX_HEIGHT_CLASS,
  SMALL_ROW_MIN_HEIGHT_CLASS,
  MEDIUM_ROW_MIN_HEIGHT_CLASS,
  TALL_ROW_MIN_HEIGHT_CLASS,
  EXTRA_TALL_ROW_MIN_HEIGHT_CLASS
} from 'style/constants'
import { PERMISSIONS } from 'helpers/auth'
import { EditorState, convertFromRaw } from 'draft-js'
import api, { APIError } from 'helpers/api'
import { isValidEmail } from 'helpers/utils'
import { recordListsEqual } from 'components/spreadsheet/helpers/record-equality'
import { canonicalJsonKey, compareJsonCells } from 'components/spreadsheet/helpers/jsonCell'

export const rounder = (digits: number) => {
  return parseInt('1' + Array(digits + 1).join('0'))
}

// Value-based comparison of two lists of sort settings, insensitive to object key
// order. Used for the "unsaved changes" flag so that adding then removing a sort (a
// net-zero change) returns the view to a clean state. recordListsEqual compares the
// union of keys, so a new attribute added to ITableViewSort is picked up automatically
// without editing this helper.
export const areSortSettingsEqual = (a: ITableViewSort[], b: ITableViewSort[]): boolean => {
  return recordListsEqual(a as unknown as Record<string, unknown>[], b as unknown as Record<string, unknown>[])
}

// Compare the working hidden-column id list against the saved baseline (a list of
// { columnId, columnName } records). Hidden columns are a SET — their order is not
// meaningful and the saved baseline can list them in a different order than the
// working copy — so this compares membership, not position.
export const areHiddenColumnsEqual = (
  hiddenColumnIds: string[],
  baseline?: ITableViewHiddenColumn[] | null
): boolean => {
  const baselineIds = Array.isArray(baseline) ? baseline.map((column) => column.columnId) : []
  if (hiddenColumnIds.length !== baselineIds.length) return false
  const baselineSet = new Set(baselineIds)
  return hiddenColumnIds.every((columnId) => baselineSet.has(columnId))
}

export const floor = (n: number, digits = 0) => {
  const r = rounder(digits)
  return Math.floor(n * r) / r
}

export const ceil = (n: number, digits = 0) => {
  const r = rounder(digits)
  return Math.ceil(n * r) / r
}

export const createCellId = (rowId: string, columnId: string, uniqueNumber: number) => {
  return `cell/${rowId}/${columnId}/${uniqueNumber}`
}

// Helper functions for accessing rowData array by columnId
// rowData is now an array indexed by column position for memory efficiency
export const getRowValue = (
  row: ITableRow,
  columnId: string,
  columnIdToIndex: Map<string, number>,
  columnStore?: CompressedColumnStore | null
): ICellValue => {
  const index = columnIdToIndex.get(columnId)
  if (index === undefined) return null
  if (row.rowData) return row.rowData[index]
  if (columnStore && row._rowIndex >= 0) return columnStore.get(row._rowIndex, index)
  return null
}

// Convert array-based rowData to object format for functions that expect { [columnId]: value }
export const rowDataArrayToObject = (
  rowData: ICellValue[] | undefined,
  indexToColumnId: string[],
  rowIndex?: number,
  columnStore?: CompressedColumnStore | null
): { [columnId: string]: ICellValue } => {
  if (!rowData && columnStore && rowIndex !== undefined && rowIndex >= 0) {
    return columnStore.getRowAsObject(rowIndex, indexToColumnId)
  }
  const result: { [columnId: string]: ICellValue } = {}
  if (!rowData) return result
  for (let i = 0; i < rowData.length && i < indexToColumnId.length; i++) {
    const columnId = indexToColumnId[i]
    if (columnId) {
      result[columnId] = rowData[i]
    }
  }
  return result
}

export const setRowValue = (
  rowData: ICellValue[] | undefined,
  columnId: string,
  value: ICellValue,
  columnIdToIndex: Map<string, number>,
  rowIndex?: number,
  columnStore?: CompressedColumnStore | null
): void => {
  const index = columnIdToIndex.get(columnId)
  if (index === undefined) return
  if (rowData) {
    rowData[index] = value
  } else if (columnStore && rowIndex !== undefined) {
    columnStore.set(rowIndex, index, value)
  }
}

export const isUIFilterActive = (filter: ITableViewFilter) => {
  if (
    (filter.filterType === IFilterType.one_of || filter.filterType === IFilterType.not_one_of) &&
    filter.multipleValues &&
    filter.multipleValues.length > 0
  ) {
    return true
  }
  if (filter.value !== '') {
    return true
  }
  if (
    filter.filterType === IFilterType.is_null ||
    filter.filterType === IFilterType.is_not_null ||
    filter.filterType === IFilterType.is_valid ||
    filter.filterType === IFilterType.is_not_valid
  ) {
    return true
  }
  return false
}

export const createApiFilterFromUIFilter = (filter: ITableViewFilter) => {
  if (filter.filterType === IFilterType.one_of || filter.filterType === IFilterType.not_one_of) {
    return {
      columnName: filter.columnName,
      filterType: filter.filterType,
      orGroup: filter.orGroup,
      multipleValues: filter.multipleValues
    }
  } else if (
    filter.filterType === IFilterType.is_null ||
    filter.filterType === IFilterType.is_not_null ||
    filter.filterType === IFilterType.is_valid ||
    filter.filterType === IFilterType.is_not_valid
  ) {
    return {
      columnName: filter.columnName,
      filterType: filter.filterType,
      orGroup: filter.orGroup,
      value: null
    }
  } else {
    return {
      columnName: filter.columnName,
      filterType: filter.filterType,
      orGroup: filter.orGroup,
      value: filter.value
    }
  }
}

// Creates mappings between column IDs and array indices
// Returns both columnIdToIndex (for lookups) and indexToColumnId (array of column IDs in order)
const createColumnMappings = (columns: ITableColumn[], columnsRow: string[]) => {
  const columnIdToIndex = new Map<string, number>()
  const indexToColumnId: string[] = []

  columnsRow.forEach((columnPublicId, idx) => {
    const column = columns.find((c) => c.publicId === columnPublicId)
    if (column) {
      columnIdToIndex.set(column.publicId, idx)
      indexToColumnId[idx] = column.publicId
    }
  })

  return { columnIdToIndex, indexToColumnId }
}

// Per-column flags for a streamed batch, computed once so the per-cell loops do no Set lookups.
const streamedColumnFlags = (
  indexToColumnId: string[],
  numCols: number,
  columnStore: CompressedColumnStore,
  columnsToTrack?: Set<string>,
  caseInsensitiveColumns?: Set<string>
) => {
  const hasColumnId = new Array<boolean>(numCols)
  const dictFlags = new Array<boolean>(numCols)
  const trackFlags = new Array<boolean>(numCols)
  const trackColIds = new Array<string>(numCols)
  const foldCaseFlags = new Array<boolean>(numCols)
  for (let ci = 0; ci < numCols; ci++) {
    const columnId = indexToColumnId[ci]
    hasColumnId[ci] = !!columnId
    dictFlags[ci] = columnStore.isDictColumn(ci)
    const shouldTrack = !!(columnsToTrack && columnId && columnsToTrack.has(columnId))
    trackFlags[ci] = shouldTrack
    foldCaseFlags[ci] = !!(caseInsensitiveColumns && columnId && caseInsensitiveColumns.has(columnId))
    if (shouldTrack) trackColIds[ci] = columnId
  }
  return { hasColumnId, dictFlags, trackFlags, trackColIds, foldCaseFlags, hasTrackedColumns: trackFlags.some(Boolean) }
}

/**
 * Add the tallies in `delta` to `target` in place and return it: rolls one batch's duplicate
 * counts into a running total (the stream consumer's, or the reducer's state).
 */
export const mergeColumnValuesCount = (target: IColumnValuesCount, delta: IColumnValuesCount): IColumnValuesCount => {
  for (const columnId in delta) {
    const counts = target[columnId] ?? (target[columnId] = {})
    const added = delta[columnId]
    for (const key in added) counts[key] = (counts[key] ?? 0) + added[key]
  }
  return target
}

// Tally one cell of a column with duplicate validation enabled.
const recordDuplicateValue = (
  seenValues: IColumnValuesCount,
  colId: string,
  resolvedValue: ICellValue,
  foldCase: boolean
) => {
  const valueKey = getDuplicateValueKey(resolvedValue?.toString() ?? '', foldCase)
  if (seenValues[colId] !== undefined && seenValues[colId][valueKey] !== undefined) {
    seenValues[colId][valueKey] += 1
  } else {
    if (seenValues[colId] === undefined) seenValues[colId] = {}
    seenValues[colId][valueKey] = 1
  }
}

const transformMsgpackRowsToSpreadsheetRows = (
  indexToColumnId: string[],
  data: ITableRowCompressed[],
  seenValues: IColumnValuesCount,
  columnStore: CompressedColumnStore,
  startRowIndex: number,
  columnsToTrack?: Set<string>,
  caseInsensitiveColumns?: Set<string>,
  lockedRowIndices?: number[]
) => {
  const rows: ITableRow[] = []
  let currentRowIndex = startRowIndex
  const numCols = data.length > 0 ? data[0].length - 2 : 0

  const { hasColumnId, dictFlags, trackFlags, trackColIds, foldCaseFlags, hasTrackedColumns } = streamedColumnFlags(
    indexToColumnId,
    numCols,
    columnStore,
    columnsToTrack,
    caseInsensitiveColumns
  )
  const lockedRowSet = lockedRowIndices?.length ? new Set(lockedRowIndices) : undefined

  // Bulk-ingest into column store (single dispatch, direct strategy access)
  columnStore.ingestBatch(data, hasColumnId, dictFlags, currentRowIndex)

  for (let i = 0; i < data.length; i++) {
    const row = data[i]

    // Track duplicate values for validation columns
    if (hasTrackedColumns) {
      for (let j = 2; j < row.length; j++) {
        const ci = j - 2
        if (!trackFlags[ci]) continue
        const resolvedValue = dictFlags[ci] ? columnStore.get(currentRowIndex, ci) : (row[j] as ICellValue)
        recordDuplicateValue(seenValues, trackColIds[ci], resolvedValue, foldCaseFlags[ci])
      }
    }
    rows.push({
      publicId: row[0] as string,
      sortOrder: row[1] as number,
      _rowIndex: currentRowIndex,
      ...(lockedRowSet?.has(i) ? { hiddenRowLocked: true } : {})
    })
    currentRowIndex++
  }

  return { rows, seenValues }
}

// The `columnar` counterpart of transformMsgpackRowsToSpreadsheetRows: the batch arrives as
// whole-column blobs, so cells go into the store one column at a time and the only per-row work
// is building the light row objects (and tallying duplicate-validation columns).
export const transformColumnarBatchToSpreadsheetRows = (
  indexToColumnId: string[],
  batch: IColumnarBatch,
  seenValues: IColumnValuesCount,
  columnStore: CompressedColumnStore,
  startRowIndex: number,
  columnsToTrack?: Set<string>,
  caseInsensitiveColumns?: Set<string>,
  lockedRowIndices?: number[]
) => {
  const rows: ITableRow[] = new Array(batch.rowCount)
  const { hasColumnId, trackFlags, trackColIds, foldCaseFlags, hasTrackedColumns } = streamedColumnFlags(
    indexToColumnId,
    batch.columns.length,
    columnStore,
    columnsToTrack,
    caseInsensitiveColumns
  )
  const lockedRowSet = lockedRowIndices?.length ? new Set(lockedRowIndices) : undefined

  columnStore.ingestColumnarBatch(batch.columns, hasColumnId, startRowIndex, batch.rowCount)

  const publicIds = decodePublicIds(batch.publicIds, batch.rowCount)
  const sortOrders = decodeSortOrders(batch.sortOrders, batch.rowCount)

  for (let i = 0; i < batch.rowCount; i++) {
    const rowIndex = startRowIndex + i
    if (hasTrackedColumns) {
      for (let ci = 0; ci < trackFlags.length; ci++) {
        if (!trackFlags[ci]) continue
        recordDuplicateValue(seenValues, trackColIds[ci], columnStore.get(rowIndex, ci), foldCaseFlags[ci])
      }
    }
    rows[i] = {
      publicId: publicIds[i],
      sortOrder: sortOrders[i],
      _rowIndex: rowIndex,
      ...(lockedRowSet?.has(i) ? { hiddenRowLocked: true } : {})
    }
  }

  return { rows, seenValues }
}

export async function* getSpreadsheetRowsStream(
  isAdmin: boolean,
  tableId: string,
  tableViewId: string,
  columns: ITableColumn[],
  processId?: string,
  columnsWithDuplicateValidation?: Set<string>,
  signal?: AbortSignal,
  totalRowsOnServer?: number
): AsyncGenerator<{
  batchRows: ITableRow[]
  seenValues: IColumnValuesCount
  columnIdToIndex: Map<string, number>
  indexToColumnId: string[]
  columnStore: CompressedColumnStore
  streamTotal?: number
}> {
  let columnIdToIndex = new Map<string, number>()
  let indexToColumnId: string[] = []
  // Email columns tally duplicates case-insensitively; every other kind exact-match.
  const caseInsensitiveColumns = getCaseInsensitiveDuplicateColumnIds(columns)
  let hasYielded = false
  let columnStore: CompressedColumnStore | null = null
  let globalRowIndex = 0
  let columnKinds: string[] = []
  let streamTotal: number | undefined

  for await (const result of api.getTableViewRowsAsync(tableId, tableViewId, isAdmin, processId, signal)) {
    if (result.type === 'header' && result.header && Array.isArray(result.header)) {
      const mappings = createColumnMappings(columns, result.header)
      columnIdToIndex = mappings.columnIdToIndex
      indexToColumnId = mappings.indexToColumnId
    } else if (result.type === 'kinds' && result.kinds && Array.isArray(result.kinds)) {
      columnKinds = result.kinds
      // Build column configs from the streamed kinds (authoritative source)
      const columnConfigs: { index: number; kind: IColumnTypes }[] = []
      for (let i = 0; i < indexToColumnId.length; i++) {
        if (indexToColumnId[i] && columnKinds[i]) {
          columnConfigs.push({ index: i, kind: columnKinds[i] as IColumnTypes })
        }
      }
      columnStore = new CompressedColumnStore(totalRowsOnServer ?? 0, columnConfigs)
    } else if (result.type === 'meta') {
      streamTotal = result.total
      // Resize store now that we know the exact row count from the query
      if (columnStore && streamTotal) {
        columnStore.ensureMinCapacity(streamTotal)
      }
    } else if (result.type === 'rows' && (result.columnar || (result.rows && Array.isArray(result.rows)))) {
      const batchRowCount = result.columnar ? result.columnar.rowCount : result.rows!.length

      // Ensure the store can hold all rows in this batch
      if (columnStore) {
        columnStore.ensureMinCapacity(globalRowIndex + batchRowCount)
      }

      // Apply dictionary updates BEFORE processing rows
      if (result.dictUpdates && columnStore) {
        for (const colIdxStr in result.dictUpdates) {
          columnStore.appendColumnDictEntries(Number(colIdxStr), result.dictUpdates[colIdxStr] as ICellValue[])
        }
      }

      // Duplicate tallies for this batch only; the consumer rolls them into its running total
      // (see mergeColumnValuesCount), so counts changed by an edit meanwhile are not overwritten.
      const batchSeenValues: IColumnValuesCount = {}
      const { rows, seenValues } = result.columnar
        ? transformColumnarBatchToSpreadsheetRows(
            indexToColumnId,
            result.columnar,
            batchSeenValues,
            columnStore!,
            globalRowIndex,
            columnsWithDuplicateValidation,
            caseInsensitiveColumns,
            result.lockedRowIndices
          )
        : transformMsgpackRowsToSpreadsheetRows(
            indexToColumnId,
            result.rows!,
            batchSeenValues,
            columnStore!,
            globalRowIndex,
            columnsWithDuplicateValidation,
            caseInsensitiveColumns,
            result.lockedRowIndices
          )
      globalRowIndex += rows.length

      hasYielded = true
      yield { batchRows: rows, seenValues, columnIdToIndex, indexToColumnId, columnStore: columnStore!, streamTotal }
    }
  }

  // For empty tables, yield the column mappings with empty rows so the consumer
  // can still set up columnIdToIndex and indexToColumnId correctly
  if (!hasYielded && columnIdToIndex.size > 0) {
    yield {
      batchRows: [],
      seenValues: {},
      columnIdToIndex,
      indexToColumnId,
      columnStore: columnStore!,
      streamTotal
    }
  }
}

// Build runtime filters from a slicer selection map. Returns ITableViewFilter[] in the
// shape the spreadsheet's client-side filterRows expects, so they can be merged with
// the user's persisted filterSettings without any backend involvement.
//
// A column is matched in one of two ways, yielding at most one filter per column:
//   - Linked column: a select/lookup column that reads FROM a slicer source via
//     kindOptions.tableOptions.columnId (downstream tables).
//   - Source column: the column IS the slicer source itself (publicId equals the source
//     column id). This filters the slicer's own source table when it is embedded in the
//     document. publicIds are globally unique, so only the source table holds this column.
// The linked match takes precedence; if it has no selection we fall back to the column's
// own publicId. These never double-fire: a downstream linked column never has
// publicId === sourceColumnId.
export const buildSlicerRuntimeFilters = (
  viewColumns: Pick<ITableViewColumn, 'publicId' | 'name' | 'kindOptions'>[] | undefined,
  slicerSelections: Record<string, string[]> | undefined
): ITableViewFilter[] => {
  if (!slicerSelections || !viewColumns) return []
  const filters: ITableViewFilter[] = []
  for (const col of viewColumns) {
    const linkedSourceId = col.kindOptions?.tableOptions?.columnId
    const matchedSourceId = linkedSourceId && slicerSelections[linkedSourceId]?.length ? linkedSourceId : col.publicId
    const values = slicerSelections[matchedSourceId]
    if (!values || values.length === 0) continue
    filters.push({
      columnId: col.publicId,
      columnName: col.name,
      filterType: IFilterType.one_of,
      orGroup: 'main',
      value: '',
      multipleValues: values
    })
  }
  return filters
}

// Merge slicer runtime filters into the user's saved filters. Filters in the same orGroup are
// AND-ed while distinct orGroups are OR-ed (see filterRows), so simply appending slicer filters
// to 'main' would make them an OR alternative to any saved or-group — widening the view instead
// of narrowing it. Distributing them into every or-group keeps slicers a pure constraint:
// (A OR B) AND s === (A AND s) OR (B AND s).
export const mergeSlicerFilters = (
  userFilters: ITableViewFilter[],
  slicerFilters: ITableViewFilter[]
): ITableViewFilter[] => {
  if (slicerFilters.length === 0) return userFilters
  if (userFilters.length === 0) return slicerFilters
  const orGroups = [...new Set(userFilters.map((filter) => filter.orGroup))]
  return [...userFilters, ...orGroups.flatMap((orGroup) => slicerFilters.map((filter) => ({ ...filter, orGroup })))]
}

// The filter has to be any here to avoid type conflicts
// in rest of spreadsheet
export const mapUnknownFilterTypes = (filter: any) => {
  return {
    ...filter,
    filterType:
      filter.filterType === 'is_not' ? IFilterType.neq : filter.filterType === 'is' ? IFilterType.eq : filter.filterType
  }
}

// Get missing columns for personal views using the parent view's columns
// This is used when tableDetails.columns is not available (non-admin in process)
export const getMissingColumnsFromParentView = (
  hiddenColumnIds: string[],
  parentViewColumns: ITableViewColumn[]
): ITableViewColumn[] => {
  return parentViewColumns
    .filter((column: ITableViewColumn) => hiddenColumnIds.includes(column.publicId))
    .map((column: ITableViewColumn) => {
      return {
        ...column,
        required: false,
        sortOrder: -1,
        formWidth: null
      }
    })
}

export interface HiddenColumnsFromParentResult {
  hiddenColumnIds: string[]
  missingColumns: ITableViewColumn[]
  hiddenColumnsArray: { columnId: string; columnName: string }[]
}

// Calculate hidden columns by comparing view columns to a parent view's columns
// Used for personal views in process where we need to know which columns from the parent are hidden
export const calculateHiddenColumnsFromParent = (
  viewColumns: ITableViewColumn[],
  parentViewColumns: ITableViewColumn[]
): HiddenColumnsFromParentResult => {
  const viewColumnIds = viewColumns.map((column) => column.publicId)

  const hiddenColumnIds = parentViewColumns
    .filter((parentColumn) => !viewColumnIds.includes(parentColumn.publicId))
    .map((column) => column.publicId)

  const missingColumns = getMissingColumnsFromParentView(hiddenColumnIds, parentViewColumns)

  const hiddenColumnsArray = missingColumns.map((col) => ({
    columnId: col.publicId,
    columnName: col.name
  }))

  return {
    hiddenColumnIds,
    missingColumns,
    hiddenColumnsArray
  }
}

export const getMissingColumns = (hiddenColumns: string[], spreadsheetData: ISpreadsheetData) => {
  const missingColumns: ITableViewColumn[] = spreadsheetData['tableDetails']['columns']
    .filter((column: ITableColumn) => hiddenColumns.includes(column.publicId))
    .map((column: ITableColumn) => {
      // Keep validation in sync
      // If you are keeping validations in sync you need to find
      // the column in another view
      let sourceColumn: ITableViewColumn | undefined
      for (let i = 0; i < spreadsheetData['tableView'].length; i++) {
        const view = spreadsheetData['tableView'][i]
        sourceColumn = view.columns
          .filter((col) => col.sortOrder !== -1)
          .find((col) => col.publicId === column.publicId)

        if (sourceColumn) {
          // If the source column is found, we can stop searching
          break
        }
      }

      return {
        ...column,
        required: false,
        sortOrder: -1,
        stringValidation:
          sourceColumn && spreadsheetData['tableDetails'].keepValidationsInSync ? sourceColumn.stringValidation : null,
        validationMessage:
          sourceColumn && spreadsheetData['tableDetails'].keepValidationsInSync ? sourceColumn.validationMessage : null,
        hardValidation:
          sourceColumn && spreadsheetData['tableDetails'].keepValidationsInSync ? sourceColumn.hardValidation : false,
        validationNoBlanks:
          sourceColumn && spreadsheetData['tableDetails'].keepValidationsInSync
            ? sourceColumn.validationNoBlanks
            : false,
        validationNoDuplicates:
          sourceColumn && spreadsheetData['tableDetails'].keepValidationsInSync
            ? sourceColumn.validationNoDuplicates
            : false,
        displayValidationError:
          sourceColumn && spreadsheetData['tableDetails'].keepValidationsInSync
            ? sourceColumn.displayValidationError
            : false,
        blockInvalidSelectValues:
          sourceColumn && spreadsheetData['tableDetails'].keepValidationsInSync
            ? sourceColumn.blockInvalidSelectValues
            : true,
        formWidth: null
      }
    })

  return missingColumns
}

export const getSpreadsheetData = async (
  tableId: string,
  tableViewId: string,
  processId?: string,
  process?: IProcessObject
): Promise<ISpreadsheetData> => {
  const data = getInitialSpreadsheetState()
  data['tableDetails']['publicId'] = tableId
  data['activeTableView'] = tableViewId

  try {
    const tableDetails = await api.getTable(tableId, false)
    const viewDetails = await api.getTableView(tableViewId)

    // Validate the view belongs to the table in the URL
    if (viewDetails.data.documentTable?.publicId && viewDetails.data.documentTable.publicId !== tableId) {
      data['errorStatusCode'] = 404
      return data
    }

    const commentStats = await api.getTableComments(tableId)

    data['tableDetails']['name'] = tableDetails.data.name
    data['tableDetails']['permissionLevel'] = tableDetails.data.permissionLevel
    data['tableDetails']['keepValidationsInSync'] = tableDetails.data.keepValidationsInSync
    data['tableDetails']['keepColoursInSync'] = tableDetails.data.keepColoursInSync
    data['tableDetails']['isDeleted'] = tableDetails.data.isDeleted
    data['tableDetails']['deletedAt'] = tableDetails.data.deletedAt
    data['tableDetails']['columns'] = tableDetails.data.columns
    data['tableDetails']['defaultViewId'] = tableDetails.data.defaultViewId
    data['tableDetails']['isSynced'] = tableDetails.data.isSynced
    data['tableDetails']['syncProvider'] = tableDetails.data.syncProvider
    data['tableDetails']['syncType'] = tableDetails.data.syncType
    data['tableDetails']['isSyncing'] = tableDetails.data.isSyncing
    data['tableDetails']['lastSync'] = tableDetails.data.lastSync
    data['tableDetails']['syncHourlyFrequency'] = tableDetails.data.syncHourlyFrequency
    data['tableDetails']['allowComments'] = tableDetails.data.allowComments
    data['tableDetails']['projectPublicId'] = tableDetails.data.projectPublicId
    data['tableDetails']['failedSyncAttempts'] = tableDetails.data.failedSyncAttempts
    data['tableDetails']['syncProjectIds'] = tableDetails.data.syncProjectIds
    data['tableDetails']['syncViewIds'] = tableDetails.data.syncViewIds
    data['tableDetails']['syncAllHubs'] = tableDetails.data.syncAllHubs
    data['tableDetails']['joins'] = tableDetails.data.joins

    const isAdmin = data.tableDetails.permissionLevel >= PERMISSIONS.owner
    const isContributor = isAdmin || viewDetails.data.permissionLevel >= PERMISSIONS.contributor

    data['viewDetails'] = {
      ...viewDetails.data,
      filterSettings: viewDetails.data.filterSettings.map(mapUnknownFilterTypes),
      colourSettings: viewDetails.data.colourSettings.map(mapUnknownFilterTypes),
      displayValidationErrorRows: isAdmin && !process ? viewDetails.data.displayValidationErrorRows : 0
    }

    data['originalViewColumns'] = viewDetails.data.columns.map((column: ITableViewColumn) => column.name)
    const columnWidths = calculateFrozenColumnWidths(
      data['viewDetails']['columns'],
      data['viewDetails']['frozenIndex'],
      data['userConfiguration']['hiddenColumns']
    )
    data['columnWidths'] = columnWidths
    data['comments'] = commentStats.data

    data['isAdmin'] = isAdmin
    data['isContributor'] = isContributor
    data['totalRowsOnServer'] = viewDetails.metadata.total!

    if (isAdmin && !processId) {
      const tableViews: ResponsePayload<ITableViewWithColumns[]> = await api.getTableViews(tableId, {
        excludePersonalViews: false,
        ignoreColumns: true
      })

      // Compute hidden + missing columns for the active view only — the rest are hydrated lazily on switch.
      const activeHiddenColumns = data['tableDetails']['columns']
        .filter(
          (column: ITableColumn) =>
            !viewDetails.data.columns.map((c: ITableViewColumn) => c.publicId).includes(column.publicId)
        )
        .map((column: ITableColumn) => ({ columnId: column.publicId, columnName: column.name }))

      const activeMissingColumns = getMissingColumns(
        activeHiddenColumns.map((col) => col.columnId),
        { ...data, tableView: [viewDetails.data as ITableViewWithColumns] }
      )

      data['tableView'] = tableViews.data.map((tableView: ITableViewWithColumns) => {
        if (tableView.publicId === viewDetails.data.publicId) {
          return {
            ...tableView,
            ...viewDetails.data,
            filterSettings: viewDetails.data.filterSettings.map(mapUnknownFilterTypes),
            colourSettings: viewDetails.data.colourSettings.map(mapUnknownFilterTypes),
            chartSettings: viewDetails.data.chartSettings,
            hiddenColumns: activeHiddenColumns,
            columns: viewDetails.data.columns.concat(activeMissingColumns),
            columnsHydrated: true
          }
        }
        return {
          ...tableView,
          filterSettings: tableView.filterSettings.map(mapUnknownFilterTypes),
          colourSettings: tableView.colourSettings.map(mapUnknownFilterTypes),
          hiddenColumns: [],
          columns: []
        }
      })
    }

    // If we are in a process then get personal views
    else {
      const personalViews: ResponsePayload<ITableViewWithColumns[]> = await api.getTableViews(tableId, {
        onlyPersonalViews: true
      })

      // Need to also append the view we are in
      personalViews.data.push(viewDetails.data as ITableViewWithColumns)

      data['tableView'] = personalViews.data
        .filter((personalView) => personalView.columns)
        .map((tableView: ITableViewWithColumns) => {
          // Find the configuration parent view from the personal views list
          const configurationParentView = personalViews.data.find(
            (view) => view.publicId === tableView.configurationParentView?.publicId
          )

          // Calculate hidden columns: columns in parent view but not in this personal view
          const hiddenColumns = configurationParentView
            ? configurationParentView.columns
                .filter(
                  (parentColumn: ITableViewColumn) =>
                    !tableView.columns
                      .map((column: ITableViewColumn) => column.publicId)
                      .includes(parentColumn.publicId)
                )
                .map((column: ITableViewColumn) => {
                  return { columnId: column.publicId, columnName: column.name }
                })
            : []

          // Calculate missing columns using the parent view's columns
          const missingColumns = configurationParentView
            ? getMissingColumnsFromParentView(
                hiddenColumns.map((column) => column.columnId),
                configurationParentView.columns
              )
            : []

          return {
            ...tableView,
            filterSettings: tableView.filterSettings.map(mapUnknownFilterTypes),
            colourSettings: tableView.colourSettings.map(mapUnknownFilterTypes),
            chartSettings: tableView.chartSettings,
            hiddenColumns,
            columns: tableView.columns.concat(missingColumns),
            columnsHydrated: true
          }
        })
    }

    if (processId && process && process.permissions) {
      data['processVariables'] = process.variables
      data['processVariableValues'] = process.variableValues
      const allTags = process.permissions
        .filter(
          (processPermission: IAccessPolicy) =>
            processPermission.accessAttribute.kind === 'tag' && processPermission.accessAttribute.tag !== undefined
        )
        .map((processPermission: IAccessPolicy) => processPermission.accessAttribute.tag) as ITag[]

      data['processTags'] = allTags

      // Find any filters and colours related to temp process variables
      data['userConfiguration']['filterSettings'] = data['viewDetails']['filterSettings'].filter(
        (filterSetting) => filterSetting.value === '{{process.public_variables}}'
      )
      data['userConfiguration']['colourSettings'] = data['viewDetails']['colourSettings'].filter(
        (colourSetting) => colourSetting.value === '{{process.public_variables}}'
      )
    }
    data['viewDetails']
  } catch (e) {
    if (e instanceof Response) {
      data['errorStatusCode'] = e.status
    } else if (e instanceof APIError) {
      data['errorStatusCode'] = e.rawCode
    } else {
      throw e
    }
  }
  return data
}

export const calculateFrozenColumnWidths = (
  columns: ITableColumn[],
  frozenIndex: number,
  hiddenColumns: string[]
): { frozenWidth: number; unfrozenWidth: number } => {
  let frozenWidth = ROW_NUMBER_COLUMN_WIDTH
  let unfrozenWidth = 0
  const unhiddenColumns = columns.filter((column: ITableColumn) => !hiddenColumns.includes(column.publicId))

  for (let i = 0; i < unhiddenColumns.length; i++) {
    const column = unhiddenColumns[i]
    if (i < frozenIndex) frozenWidth += column.width
    else unfrozenWidth += column.width
  }

  return { frozenWidth, unfrozenWidth }
}

export const arrayMove = (arr: any[], fromIndex: number, toIndex: number) => {
  const element = arr[fromIndex]
  arr.splice(fromIndex, 1)
  arr.splice(toIndex, 0, element)
}

export const validateField = (
  kind: IColumnTypes,
  value: ICellValue,
  fieldName: string
): IResultFieldValidation | IResultFieldValidation[] => {
  if (value === null) return { pass: true, error: '' }
  if (TEXT_COLUMN_TYPES.includes(kind)) {
    return validateTextField(kind, value as string, fieldName)
  } else if (NUMBER_COLUMN_TYPES.includes(kind)) {
    return validateNumericField(kind, value as string, fieldName)
  } else if (DATE_COLUMN_TYPES.includes(kind)) {
    return validateDateTimeField(kind, value as string, fieldName)
  } else if (LINK_COLUMN_TYPES.includes(kind)) {
    if (kind === 'link') {
      return validateSingleLinkField(value as string)
    } else {
      return (value as string[]).map(() => validateSingleLinkField(value as string))
    }
  } else if (kind === 'select') {
    const isValid = value === null || typeof value === 'string'
    return {
      pass: value === null || typeof value === 'string',
      error: isValid
        ? ''
        : `'${value}' isn't one of the options in column '${fieldName}'. Choose an option from the list.`
    }
  } else if (kind === 'checkbox') {
    const isValid = value === 'true' || value === 'false'
    return {
      pass: isValid,
      error: isValid ? '' : `'${value}' isn't a valid value for Checkbox column '${fieldName}'. Use true or false.`
    }
  }
  return { pass: false, error: `This value can't be entered in column '${fieldName}'.` }
}

export const validateTextField = (kind: IColumnTypes, value: string, fieldName: string) => {
  switch (kind) {
    case 'text':
    case 'tag':
    case 'variable':
      return { pass: true, error: '' }
    case 'email': {
      const validEmail = isValidEmail(value)
      return {
        pass: validEmail,
        error: validEmail
          ? ''
          : `'${value}' isn't a valid email address. Column '${fieldName}' needs an email like name@example.com.`
      }
    }
    case 'phone': {
      const validPhone = /^\+?(([0-9]|x)+)?$/.test(value)
      return {
        pass: validPhone,
        error: validPhone
          ? ''
          : `'${value}' isn't a valid phone number. Column '${fieldName}' accepts digits only, with an optional + at the start and no spaces.`
      }
    }
    default:
      return { pass: false, error: `This value can't be entered in column '${fieldName}'.` }
  }
}

export const validateNumericField = (kind: IColumnTypes, value: string | number, fieldName: string) => {
  if (value === '') return { pass: true, error: '' }
  const numberValue = Number(value)
  if (Number.isNaN(numberValue) || (kind === 'integer' && !Number.isInteger(numberValue))) {
    const expected = kind === 'integer' ? 'whole number' : 'number'
    return { pass: false, error: `'${value}' isn't a ${expected}. Column '${fieldName}' only accepts ${expected}s.` }
  }
  return { pass: true, error: '' }
}

const invalidDateMessage = (value: string, fieldName: string) =>
  `'${value}' isn't a valid date. Enter a date like 2026-12-31 in column '${fieldName}'.`

export const validateDateTimeField = (kind: IColumnTypes, value: string, fieldName: string) => {
  if (typeof value === 'string') {
    const dateValue = new Date(value)
    const validDate = !Number.isNaN(dateValue.getTime())
    return { pass: validDate, error: validDate ? '' : invalidDateMessage(value, fieldName) }
  }
  return { pass: false, error: invalidDateMessage(value, fieldName) }
}

// Mirrors the backend `is_valid_iso8601_date` regex in api/app/dao/table/field.py.
// The backend date/datetime fields reject anything that doesn't match this, so the
// frontend must use the same rule (not the looser `new Date(...)`) to decide whether
// a stored default value would be accepted.
const ISO8601_DATE_REGEX =
  /^(-?(?:[1-9][0-9]*)?[0-9]{4})-((0[1-9]|1[0-2]))-((0[1-9]|[12][0-9]|3[01]))(T([01][0-9]|2[0-3]):([0-5][0-9])(:([0-5][0-9]|60)(\.\d+)?)?([zZ]|([+-])([01][0-9]|2[0-3]):([0-5][0-9]))?)?$/

export const isValidIso8601Date = (value: string): boolean => ISO8601_DATE_REGEX.test(value)

// A column's default value is always stored as a string and is run through the field's
// `prepare_value_for_db` when a new row is added. For most kinds any string is accepted,
// but date/datetime, number, checkbox and json kinds reject values that don't match the
// type — so an old default left behind after a type change (e.g. "N/A" on a column
// switched to date) is silently ignored on insert. Returns an error message to flag such
// a default in the UI, or null when the value is valid (or the kind accepts any string).
// Kept in lockstep with api/app/dao/table/field.py.
export const getDefaultValueError = (kind: IColumnTypes, value: string | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') return null

  if (DATE_COLUMN_TYPES.includes(kind)) {
    return isValidIso8601Date(value)
      ? null
      : `Default value must be ${kind === 'datetime' ? 'a date and time like 2026-03-04T10:30' : 'a date like 2026-03-04'}`
  }
  if (NUMBER_COLUMN_TYPES.includes(kind)) {
    return Number.isFinite(Number(value)) ? null : 'Default value must be a number'
  }
  if (kind === 'checkbox') {
    return value === 'true' || value === 'false' ? null : 'Default value must be true or false'
  }
  if (kind === 'json') {
    try {
      const parsed = JSON.parse(value)
      const isObject = parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      return isObject ? null : 'Default value must be a JSON object'
    } catch {
      return 'Default value must be valid JSON'
    }
  }
  // text, tag, variable, email, phone, link, select, multiselect — and the structured
  // kinds (vote/attachment/multilink) whose default field is hidden — accept any string.
  return null
}

export const validateSingleLinkField = (value: null | string | string[]) => {
  if (!value) {
    return {
      pass: true,
      error: ''
    }
  }

  const isValid = true
  return { pass: isValid, error: isValid ? '' : `${value} is an invalid value.` }
}

export const isRowInGroup = (row: unknown): row is ITableRowInGroup =>
  row !== undefined && row !== null && (row as ITableRowInGroup).isGrouped ? true : false

export const isGroupRowCreate = (row: unknown): row is IGroupRowCreate =>
  row !== null && row !== undefined && (row as IGroupRowCreate).isAddRow

export const isCollapsedGroupRow = (row: unknown): row is IGroupCollapsed => (row as IGroupCollapsed).isCollapsedRow

export const isTreeGroupRow = (row: unknown): row is ITreeGroupRow =>
  row !== null && row !== undefined && (row as ITreeGroupRow).isTreeGroupRow === true

/**
 * A row coming from the viewport can be:
 *   • a real data row   (ITableRow)               → has `publicId`
 *   • a group header    (ITableRowInGroup / …)    → no `publicId`
 *   • a collapsed stub  (IGroupCollapsed / …)     → no `publicId`
 *
 * The type-guard below narrows the union to the first case.
 */

export const isDataRow = (
  row: ITableRow | ITableRowInGroup | IGroupRowCreate | IGroupCollapsed | ITreeGroupRow
): row is ITableRow & { publicId: string } => {
  return !!(row as ITableRow).publicId // truthy check is enough
}

/** Check whether a row at the given index is a non-data row (group header, add-row, collapsed, tree group). */
export const isNonDataRow = (rows: readonly unknown[], index: number): boolean => {
  const row = rows[index]
  if (!row) return true
  return isGroupRowCreate(row) || isCollapsedGroupRow(row) || isTreeGroupRow(row) || !(row as ITableRow).publicId
}

/**
 * Starting from `index`, walk in `direction` (+1 or -1) to find the nearest data row.
 * Returns the original index if it's already a data row, or -1 if none found.
 */
export const findNearestDataRow = (rows: readonly unknown[], index: number, direction: 1 | -1): number => {
  for (let i = index; i >= 0 && i < rows.length; i += direction) {
    if (!isNonDataRow(rows, i)) return i
  }
  return -1
}

export const isSummaryUser = (value: ICellValue): value is ISummaryUser => {
  const test = value as ISummaryUser

  if (test !== null && test.name && test.email) {
    return true
  }
  return false
}

export const getRowHeightVariable = (rowHeight: number) => {
  return ROW_HEIGHTS[rowHeight as keyof typeof ROW_HEIGHTS] ?? ROW_HEIGHTS[0]
}

export const getRowHeightClassName = (rowHeight: number) => {
  if (rowHeight === rowHeights.Small) return SMALL_ROW_HEIGHT_CLASS
  else if (rowHeight === rowHeights.Medium) return MEDIUM_ROW_HEIGHT_CLASS
  else if (rowHeight === rowHeights.Tall) return TALL_ROW_HEIGHT_CLASS
  else return EXTRA_TALL_ROW_HEIGHT_CLASS
}

export const getRowMaxHeightClassName = (rowHeight: number) => {
  if (rowHeight === rowHeights.Small) return SMALL_ROW_MAX_HEIGHT_CLASS
  else if (rowHeight === rowHeights.Medium) return MEDIUM_ROW_MAX_HEIGHT_CLASS
  else if (rowHeight === rowHeights.Tall) return TALL_ROW_MAX_HEIGHT_CLASS
  else return EXTRA_TALL_ROW_MAX_HEIGHT_CLASS
}

export const getRowMinHeightClassName = (rowHeight: number) => {
  if (rowHeight === rowHeights.Small) return SMALL_ROW_MIN_HEIGHT_CLASS
  else if (rowHeight === rowHeights.Medium) return MEDIUM_ROW_MIN_HEIGHT_CLASS
  else if (rowHeight === rowHeights.Tall) return TALL_ROW_MIN_HEIGHT_CLASS
  else return EXTRA_TALL_ROW_MIN_HEIGHT_CLASS
}

export const coerceToValue = (val: ICellValue, kind: IColumnTypes) => {
  if (val === null || val === undefined) {
    return ''
  } else if (kind === 'json') {
    // A JSON cell holds a parsed object, so `toString()` would collapse every row to
    // "[object Object]" — one meaningless group. canonicalJsonKey serialises with keys in
    // jsonb's order, so two cells holding the same object share a group whatever order their
    // keys were typed in. Ordering is NOT done on this string — see sortRows.
    return canonicalJsonKey(val)
  } else if (typeof val === 'string') {
    return val
  } else if (typeof val === 'number') {
    return val
  } else if (Array.isArray(val) && kind === 'vote') {
    const voteArray: IVote[] = val as IVote[]
    const positiveVotes = voteArray.filter((vote: IVote) => vote.type).length
    const negativeVotes = voteArray.filter((vote: IVote) => !vote.type).length
    return positiveVotes - negativeVotes
  } else {
    return val.toString()
  }
}

// A row carries its cells either inline (rowData) or in the shared compressed store.
const getSortCellValue = (
  row: ITableRow,
  columnIndex: number | undefined,
  columnStore?: CompressedColumnStore | null
): ICellValue => {
  if (columnIndex === undefined) return null
  if (row.rowData) return row.rowData[columnIndex]
  return columnStore ? columnStore.get(row._rowIndex, columnIndex) : null
}

export const isSortedBySortOrder = (rows: ITableRow[]): boolean => {
  for (let i = 1; i < rows.length; i++) {
    if (rows[i - 1].sortOrder > rows[i].sortOrder) return false
  }
  return true
}

export const sortRows = (
  rows: ITableRow[],
  sortBy: ITableViewSort[],
  columns: ITableViewColumn[],
  columnIdToIndex?: Map<string, number>,
  columnStore?: CompressedColumnStore | null
) => {
  if (sortBy.length === 0) {
    const sortedRows = [...rows]
    // Streamed rows already arrive ordered by sortOrder; checking is O(n) and skips an O(n log n)
    // sort that, on a million rows, blocks the main thread for hundreds of milliseconds.
    if (isSortedBySortOrder(sortedRows)) return sortedRows
    return sortedRows.sort((a, b) => a.sortOrder - b.sortOrder)
  }

  // Resolve each sort key's column once, and read and coerce each row's sort values once, up
  // front. Doing that inside the comparator meant a column scan plus a coercion per key for
  // every one of the ~n log n comparisons: on a million rows a single-key sort took tens of
  // seconds and froze the page while a view with a sort was being opened.
  const keys = sortBy.map((sort) => {
    const column = columns.find((c: ITableViewColumn) => c.publicId === sort.columnId)
    return {
      kind: column ? column.kind : ('text' as IColumnTypes),
      index: columnIdToIndex?.get(sort.columnId),
      direction: sort.direction === 'desc' ? -1 : 1
    }
  })
  const keyCount = keys.length
  const rowCount = rows.length
  // Row i's value for key j sits at i * keyCount + j. JSON cells keep their parsed value: they
  // are ordered structurally (see compareJsonCells), not by their serialised text.
  const sortValues: (string | number | ICellValue)[] = new Array(rowCount * keyCount)
  for (let i = 0; i < rowCount; i++) {
    const row = rows[i]
    for (let j = 0; j < keyCount; j++) {
      const key = keys[j]
      const raw = getSortCellValue(row, key.index, columnStore)
      sortValues[i * keyCount + j] = key.kind === 'json' ? raw : coerceToValue(raw, key.kind)
    }
  }

  const order: number[] = new Array(rowCount)
  for (let i = 0; i < rowCount; i++) order[i] = i
  order.sort((a, b) => {
    for (let j = 0; j < keyCount; j++) {
      const key = keys[j]
      const aValue = sortValues[a * keyCount + j]
      const bValue = sortValues[b * keyCount + j]
      const result =
        key.kind === 'json'
          ? compareJsonCells(aValue as ICellValue, bValue as ICellValue)
          : compareSortValues(aValue as string | number, bValue as string | number)
      if (result !== 0) return key.direction * result
    }
    // Equal on every key: keep the incoming order, as the previous stable sort did.
    return a - b
  })

  const sortedRows: ITableRow[] = new Array(rowCount)
  for (let i = 0; i < rowCount; i++) sortedRows[i] = rows[order[i]]
  return sortedRows
}

/**
 * Compares two values for sorting, treating empty strings as "sort last".
 * Returns -1 if a < b, 1 if a > b, 0 if equal.
 */
const compareSortValues = (aValue: string | number, bValue: string | number): number => {
  if (aValue === bValue) return 0
  if (aValue === '') return 1 // Empty values sort last
  if (bValue === '') return -1
  if (aValue < bValue) return -1
  if (aValue > bValue) return 1
  return 0
}

export const convertToApiFilter = (filterSettings: ITableViewFilter[]) => {
  return filterSettings.map((filter) => {
    const { value, multipleValues, ...rest } = filter
    if (filter.filterType === 'one_of' || filter.filterType === 'not_one_of') {
      return { ...rest, multipleValues } as ITableViewFilter
    }
    return { ...rest, value } as ITableViewFilter
  })
}

export const convertToApiColour = (colourSettings: ITableViewColour[]) => {
  return colourSettings.map((colour) => {
    const { value, multipleValues, ...rest } = colour
    if (colour.filterType === 'all_values') {
      return rest as ITableViewColour
    }
    if (colour.filterType === 'one_of' || colour.filterType === 'not_one_of') {
      return { ...rest, multipleValues } as ITableViewColour
    }
    return { ...rest, value } as ITableViewColour
  })
}

// The column-lock rule shape: an unconditional, column-scoped row-lock
// (filterType `all_values`, applyToEntireRow off). This is the row-lock
// representation that replaced the legacy column.locked boolean. Conditional
// rules and entire-row rules are NOT column-locks and must be left untouched.
export const isColumnLockRule = (rule: ITableViewRowLock): boolean =>
  rule.filterType === 'all_values' && !rule.applyToEntireRow

// A column is "locked" (read-only for the whole column) when it has a
// column-lock rule. Anything that used to gate on `column.locked` (e.g.
// require/lock mutual exclusion) should consult this instead.
export const isColumnLockedByRules = (rowLockSettings: ITableViewRowLock[], columnPublicId: string): boolean =>
  rowLockSettings.some((rule) => rule.columnId === columnPublicId && isColumnLockRule(rule))

export const convertToApiRowLock = (rowLockSettings: ITableViewRowLock[]) => {
  return rowLockSettings.map((rowLock) => {
    const { value, multipleValues, ...rest } = rowLock
    if (rowLock.filterType === 'all_values') {
      return rest as ITableViewRowLock
    }
    if (rowLock.filterType === 'one_of' || rowLock.filterType === 'not_one_of') {
      return { ...rest, multipleValues } as ITableViewRowLock
    }
    return { ...rest, value } as ITableViewRowLock
  })
}

// Only return the columnStackId if the chart type supports grouping. Otherwise, return null to avoid sending an invalid value to the API.
export const convertToApiChart = (chartSettings: ITableViewChart | null): ITableViewChart | null => {
  if (!chartSettings) {
    return chartSettings
  }
  if (chartSettings.chartType && CHARTS_WITH_GROUPING.includes(chartSettings.chartType)) {
    return chartSettings
  }
  return { ...chartSettings, columnStackId: null }
}

export const findRow = (rowPublicId: string, rows: ITableRow[]) => {
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].publicId === rowPublicId) {
      return rows[i]
    }
  }
  return null
}

export const isFieldValueEmpty = (kind: IColumnTypes, value: unknown) => {
  const arrayTypes = [...ATTACHMENT_COLUMN_TYPES, 'multiselect', 'multilink', 'vote']
  const stringTypes = [...TEXT_COLUMN_TYPES, 'select', 'link']
  const numberTypes = NUMBER_COLUMN_TYPES

  // General checks
  if (value === undefined || value === null) {
    return true
  }

  // For arrays check length
  if (arrayTypes.includes(kind)) {
    if ((value as unknown[]).length === 0) {
      return true
    } else {
      return false
    }
  }

  // For strings check it's not empty
  else if (stringTypes.includes(kind)) {
    if ((value as string).trim() === '') {
      return true
    } else {
      return false
    }
  }

  //  For numbers the undefined and null check is enough
  else if (numberTypes.includes(kind)) {
    return false
  }

  // For everything else check if value not falsy
  else {
    return !value ? true : false
  }
}

export const isValidOption = (column: ITableColumn, value: ICellValue, values: { [key: string]: ICellValue }) => {
  if (!column.kindOptions || value === null) {
    return false
  }
  let allOptions = column.kindOptions.manualOptions ? [...column.kindOptions.manualOptions] : []

  if (Object.keys(column.kindOptions.tableOptions).length > 0) {
    const tableOptions = column.kindOptions.tableOptions
    let cachedOptions: string[] = []
    if (Array.isArray(tableOptions.cachedOptions)) {
      cachedOptions = [...tableOptions.cachedOptions]
    } else {
      cachedOptions = resolveDependencyCachedOptions(
        tableOptions.cachedOptions as { [key: string]: string[] },
        tableOptions.dependencies,
        (columnId) => values[columnId]
      )
    }
    allOptions = allOptions.concat(cachedOptions)
  }

  // Check if there are options available
  if (allOptions.length === 0) return false

  const isValidSelectValue = column.kind === 'select' && allOptions.includes(value.toLocaleString())
  const isValidMultiselectValue =
    column.kind === 'multiselect' &&
    Array.isArray(value) &&
    (value as string[])
      .map((currentVal) => {
        return allOptions.includes(currentVal.toLocaleString())
      })
      .every(Boolean)

  return isValidSelectValue || isValidMultiselectValue
}

/**
 * Every value a linked select's source offers, whatever the dependency: its cached options, listed, or grouped by
 * the dependency's value ({ France: ['Lyon'], UK: [...] }). A hand-entered option not among them is missing from the
 * source; Edit column and a cell's dropdown both tell them apart by this, so they agree.
 */
export const sourceValuesOf = (cached: string[] | { [key: string]: string[] } | null | undefined): Set<string> =>
  new Set(Array.isArray(cached) ? cached : Object.values(cached ?? {}).flat())

/**
 * A select's kind options as they're saved: without the fields the server manages, its id and the options it
 * caches from a source table.
 */
export const kindOptionsToSave = <T extends object>(kindOptions: T): T => {
  const payload = JSON.parse(JSON.stringify(kindOptions))
  delete payload.publicId
  if (payload.tableOptions) delete payload.tableOptions.cachedOptions
  return payload
}

/**
 * Resolve cached options from a dependency-grouped cachedOptions dictionary,
 * handling multiselect (array) dependency source values via cross-product
 * key expansion and union lookup.
 */
export const resolveDependencyCachedOptions = (
  cachedOptionsDict: { [key: string]: string[] },
  dependencies: { columnId: string }[],
  getCellValue: (columnId: string) => ICellValue | undefined
): string[] => {
  const valueGroups: string[][] = []

  const dictKeys = Object.keys(cachedOptionsDict)
  const depCount = dependencies.length

  for (let i = 0; i < depCount; i++) {
    const cellValue = getCellValue(dependencies[i].columnId)
    if (Array.isArray(cellValue)) {
      if (cellValue.length === 0) {
        // Empty multiselect → wildcard for this axis.
        // Extract all unique values at this position from the dictionary keys
        // so other dependencies still filter correctly.
        const wildcardValues = new Set<string>()
        for (const dictKey of dictKeys) {
          const parts = dictKey.split(GROUP_KEY_SPLIT)
          if (parts.length === depCount && parts[i]) {
            wildcardValues.add(parts[i])
          }
        }
        valueGroups.push(wildcardValues.size > 0 ? Array.from(wildcardValues) : [])
      } else {
        valueGroups.push(cellValue.map(String))
      }
    } else {
      valueGroups.push([`${cellValue}`])
    }
  }

  // Cross-product of all value groups to get all possible keys
  const keys = valueGroups.reduce<string[]>(
    (acc, group) => acc.flatMap((prefix) => group.map((val) => (prefix ? `${prefix}${GROUP_KEY_SPLIT}${val}` : val))),
    ['']
  )

  // Union options for all keys
  const result = new Set<string>()
  for (const key of keys) {
    const opts = cachedOptionsDict[key]
    if (opts) {
      for (const opt of opts) result.add(opt)
    }
  }
  return Array.from(result)
}

// Convert a row that has column ID keys to column name keys
export const convertRowDataToNameValueMap = (columns: ITableColumn[], values: Record<string, ICellValue>) => {
  const output = Object.keys(values).reduce(
    (accumulator, columnId) => {
      const column = columns.find((c) => c.publicId === columnId)
      if (column) {
        accumulator[column.name] = values[columnId]
      }
      return accumulator
    },
    {} as Record<string, ICellValue>
  )

  return output
}

// Convert a row that has column name keys to column ID keys (opposite of above function)
export const convertRowDataToIdValueMap = (columns: ITableColumn[], values: Record<string, ICellValue>) => {
  const output = Object.keys(values).reduce(
    (accumulator, columnName) => {
      const column = columns.find((c) => c.name === columnName)
      if (column) accumulator[column.publicId] = values[columnName]
      return accumulator
    },
    {} as Record<string, ICellValue>
  )

  return output
}

// Convert API response rowData (object with column names as keys) to array format
// Uses the columnIdToIndex mapping to place values at correct indices
export const convertNameMapToRowDataArray = (
  columns: ITableColumn[],
  values: Record<string, ICellValue>,
  columnIdToIndex: Map<string, number>,
  indexToColumnId: string[]
): ICellValue[] => {
  const rowData: ICellValue[] = new Array(indexToColumnId.length).fill(null)

  Object.keys(values).forEach((columnName) => {
    const column = columns.find((c) => c.name === columnName)
    if (column) {
      const index = columnIdToIndex.get(column.publicId)
      if (index !== undefined) {
        rowData[index] = values[columnName]
      }
    }
  })

  return rowData
}

export const isEditorEmpty = (editorContent: EditorContent) => {
  if (editorContent) {
    const editorState = EditorState.createWithContent(convertFromRaw(editorContent.content))
    return !editorState.getCurrentContent().hasText()
  }
  return true
}

export const isSameValueStringValue = (v1: string, v2: number | string | boolean | null) => {
  // empty string and null is evaulated as the same
  if (v1 === '' && v2 === null) {
    return true
    // compare string with string version of number
  } else if (typeof v2 === 'number') {
    if ((v1 === null && v2 !== null) || (v1 !== null && v2 === null)) {
      return false
    } else {
      // Empty string and 0 are evaluated as equal values when transformed into numbers.
      // We need to make sure that an empty value in the text field means null and that 0 is an actual value and not an empty one
      return v1.toString() === v2.toString() || (Number(v1) === Number(v2) && !(v1 === '' && Number(v2) === 0))
    }

    // compare strings
  } else {
    return v1 === v2
  }
}

export const buildNewColumn = (
  spreadsheetData: ISpreadsheetData,
  sortOrder: number,
  projectId: string,
  headerBackgroundColor: string,
  headerTextColor: string
) => {
  const fieldRegex = new RegExp('^Column [0-9]+$')
  const fields = (spreadsheetData.tableDetails.columns || [])
    .filter((column) => fieldRegex.test(column.name))
    .map((column) => Number(column.name.split(' ').pop()))
  fields.sort((a, b) => a - b)
  const lastFieldNumber = fields.pop()
  const fieldNumber = lastFieldNumber ? lastFieldNumber + 1 : 1
  const column: Omit<ITableViewColumn, 'publicId' | 'kindOptions' | 'isSynced'> = {
    name: `Column ${fieldNumber}`,
    sortOrder: sortOrder,
    isIndexed: false,
    width: 150,
    kind: DEFAULT_COLUMN_TYPE,
    description: null,
    required: false,
    script: null,
    thousandSeparator: true,
    displayLink: true,
    decimalPlaces: 2,
    dateFormat: null,
    defaultValue: null,
    scriptEnabled: false,
    formula: null,
    formulaEnabled: false,
    aggregate: IAggregateTypes.None,
    stringValidation: null,
    validationMessage: null,
    hardValidation: false,
    validationNoBlanks: false,
    validationNoDuplicates: false,
    displayValidationError: true,
    blockInvalidSelectValues: true,
    isJoined: false,
    headerBackgroundColor: headerBackgroundColor,
    headerTextColor: headerTextColor,
    exportWidth: null,
    formWidth: null
  }
  const payload = { ...column, context: { projectId } }
  return payload
}

export const getColumnsAsAdmin = (spreadsheetData: ISpreadsheetData) => {
  // Keep order of the columns in the view. Add missing columns on view at the end
  const missingColumns: ITableColumn[] = []
  spreadsheetData.tableDetails.columns.map((col) => {
    const found = spreadsheetData.viewDetails.columns.find((viewColumn) => viewColumn.publicId === col.publicId)
    if (!found) {
      missingColumns.push(col)
    }
  })
  return [...spreadsheetData.viewDetails.columns, ...missingColumns] as ITableColumn[]
}

export const getColumnsPerUserRole = (spreadsheetData: ISpreadsheetData) => {
  // Synthetic column views (e.g. transpose) have derived columns — skip the admin
  // missing-columns merge to avoid mixing synthetic and real columns
  if (hasSyntheticColumns(spreadsheetData.viewDetails.type)) {
    return spreadsheetData.viewDetails.columns as ITableColumn[]
  }
  return spreadsheetData.isAdmin
    ? getColumnsAsAdmin(spreadsheetData)
    : (spreadsheetData.viewDetails.columns as ITableColumn[])
}

/** Original (pre-transformation) data for the current view.
 *  In transformed views (transpose, etc.), returns the real data before transformation.
 *  In standard spreadsheet views, returns the current data directly. */
export interface IOriginalData {
  columns: ITableViewColumn[]
  rows: ITableRow[]
  columnIdToIndex: Map<string, number>
  columnStore: CompressedColumnStore | null
  coordinateMap: IViewCoordinateMap | null
}

export const getOriginalData = (spreadsheetData: ISpreadsheetData): IOriginalData => {
  const ctx = spreadsheetData.viewContext
  if (ctx) {
    return {
      columns: ctx.originalColumns,
      rows: ctx.originalRows,
      columnIdToIndex: ctx.originalColumnIdToIndex,
      columnStore: ctx.originalColumnStore,
      coordinateMap: ctx.coordinateMap
    }
  }
  return {
    columns: spreadsheetData.viewDetails.columns,
    rows: spreadsheetData.rows,
    columnIdToIndex: spreadsheetData.columnIdToIndex,
    columnStore: spreadsheetData.columnStore,
    coordinateMap: null
  }
}

/** Get the original columns typed as ITableColumn[] (for menus that need the base column type).
 *  For standard views, applies the admin-role merge so admins see columns that exist on the
 *  table but are missing from the current view (needed for colour/filter/hide menus). */
export const getOriginalColumns = (spreadsheetData: ISpreadsheetData): ITableColumn[] => {
  if (spreadsheetData.viewContext) {
    return spreadsheetData.viewContext.originalColumns as ITableColumn[]
  }
  return getColumnsPerUserRole(spreadsheetData)
}

/** Get the original columns typed as ITableViewColumn[] (for menus that need locked, required, etc.). */
export const getOriginalViewColumns = (spreadsheetData: ISpreadsheetData): ITableViewColumn[] => {
  return getOriginalData(spreadsheetData).columns
}
